import { SdkMessageTypes } from '../constants';
import { HostedWebContentSdkService } from '../hosted-web-content-sdk';
import { ICalendarService, OpenCalendarItemParams, ComposeMeetingParams, ITelemetryService } from '../interfaces';
import { ILegacyLinkHandler } from './deep-link-message-handler';
import { BaseMessageHandler } from './base-message-handler';
import { onStatusError } from './messageHandlingUtils';
/**
 * Responsible for calender module related message requests.
 */
export class CalendarMessageHandler extends BaseMessageHandler {
  constructor(
    appSdkHost: HostedWebContentSdkService,
    protected calendarService?: ICalendarService,
    telemetryService?: ITelemetryService
  ) {
    super(appSdkHost, telemetryService);
  }

  public initHandlers(): void {
    this.registerHandler(
      SdkMessageTypes.calendar.openCalendarItem,
      async (_, params: OpenCalendarItemParams) => {
        await this.calendarService!.openCalendarItem(params);
        return [true];
      },
      {
        errorCallback: (error, scenario) => onStatusError(SdkMessageTypes.calendar.openCalendarItem, error, scenario),
      }
    );
    this.registerHandler(
      SdkMessageTypes.calendar.composeMeeting,
      async (_, params: ComposeMeetingParams) => {
        await this.calendarService!.composeMeeting(params);
        return [true];
      },
      {
        errorCallback: (error, scenario) => onStatusError(SdkMessageTypes.calendar.composeMeeting, error, scenario),
      }
    );
  }

  protected validateDependencies() {
    if (!this.calendarService) {
      throw 'No calendarService provided';
    }
  }
}

export class CalendarLegacyLinkHandler implements ILegacyLinkHandler<ComposeMeetingParams> {
  constructor(private calendarService?: ICalendarService) {}

  public tryGetDataFromLink(url: URL): ComposeMeetingParams | null {
    if (!this.calendarService) {
      return null;
    }
    if (url.origin != 'https://teams.microsoft.com' || url.pathname != '/l/meeting/new') {
      return null;
    }
    const attendees = url.searchParams.get('attendees')?.split(',');
    const startTime = url.searchParams.get('startTime') || undefined;
    const endTime = url.searchParams.get('endTime') || undefined;
    const subject = url.searchParams.get('subject') || undefined;
    const content = url.searchParams.get('content') || undefined;
    return {
      attendees,
      startTime,
      endTime,
      subject,
      content,
    };
  }

  public executeData(data: ComposeMeetingParams): Promise<void> {
    return this.calendarService!.composeMeeting(data);
  }
}
